import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import ConfirmationDialog from "@/components/ConfirmationDialog"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  useGetMedicationRemindersQuery,
  useSelfQuery,
  useSetMedicationRemindersMutation,
} from "@/data/queries"
import MedicationReminders from "@/screens/onboarding/components/MedicationReminders"
import { useUnsavedChangesGuard } from "@/util/hooks/useUnsavedChangesGuard"
import { useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

const sameTimes = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  return a.every((time, index) => time === b[index])
}

type UpdateRemindersFormProps = {
  patientId: string
  initialReminderTimes: string[]
}

// Split out so `initialReminderTimes` seeds useState on mount only — later
// refetches must not clobber in-progress edits.
const UpdateRemindersForm = ({ patientId, initialReminderTimes }: UpdateRemindersFormProps) => {
  const navigate = useNavigate()
  const { mutateAsync: setReminders, isPending: isSaving } = useSetMedicationRemindersMutation()

  const [reminderTimes, setReminderTimes] = useState<string[]>(initialReminderTimes)
  const [savedReminderTimes, setSavedReminderTimes] = useState<string[]>(initialReminderTimes)

  const isDirty = !sameTimes(reminderTimes, savedReminderTimes)

  const { isPromptOpen, guard, confirm, cancel } = useUnsavedChangesGuard(isDirty)

  const handleSubmit = async () => {
    try {
      await setReminders({ patientId, reminderTimes })
      setSavedReminderTimes(reminderTimes)
      toast.success("Reminders saved.")
    } catch {
      toast.error("Failed to save reminders. Please try again.")
    }
  }

  return (
    <>
      <div className="flex min-h-[calc(100vh-56px)] flex-col justify-between">
        <div className="flex flex-col gap-6">
          <BackButton customClickHandler={() => guard(() => navigate(-1))} />

          <h1 className="text-2xl font-bold text-primary-contrast">Reminders</h1>
          <h2 className="text-base font-medium text-primary-contrast">Medication reminders:</h2>

          <MedicationReminders reminderTimes={reminderTimes} onChange={setReminderTimes} />
        </div>

        <Button
          type="button"
          disabled={isSaving}
          className="flex w-full gap-3 py-2 text-base"
          onClick={handleSubmit}
        >
          {isSaving && <Spinner />}
          {isSaving ? "Updating" : "Update"}
        </Button>
      </div>

      <ConfirmationDialog
        open={isPromptOpen}
        title="Unsaved Changes"
        description="You have unsaved changes that will be lost. Do you wish to proceed?"
        onConfirm={confirm}
        onCancel={cancel}
      />
    </>
  )
}

const UpdateRemindersPage = () => {
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""

  const { data: existingReminders, isPending: isLoadingReminders } = useGetMedicationRemindersQuery(
    { patientId },
  )

  return (
    <Page showAppbar={false}>
      {isLoadingReminders || existingReminders === undefined ? (
        <div className="flex flex-col gap-6">
          <BackButton />
          <h1 className="text-2xl font-bold text-primary-contrast">Reminders</h1>
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        </div>
      ) : (
        <UpdateRemindersForm patientId={patientId} initialReminderTimes={existingReminders} />
      )}
    </Page>
  )
}

export default UpdateRemindersPage
